import { useLocation } from '@solidjs/router'
import { IconAt, IconSparkles } from '@tabler/icons-solidjs'
import { AiComposer, AiMarkdown, cn, Sheet } from '@veem/ui'
import { createSignal, For, Show } from 'solid-js'
import { ASSISTANT_UNAVAILABLE_REPLY, AssistantTools, SUGGESTIONS } from './assistant-shared.tsx'

interface Message {
  id: number
  from: 'person' | 'assistant'
  text: string
}

const contextLabel = (path: string): string => {
  if (path.includes('/orders/')) return 'Це замовлення'
  if (path.includes('/orders')) return 'Список замовлень'
  if (path.includes('/warehouse')) return 'Склад'
  if (path.includes('/inbox')) return 'Мої завдання'
  return 'Поточна сторінка'
}

export const AssistantDock = (props: { open: boolean; onOpenChange: (open: boolean) => void }) => {
  const location = useLocation()
  const [value, setValue] = createSignal('')
  const [messages, setMessages] = createSignal<Message[]>([])
  let counter = 0

  const submit = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed) return
    setMessages((list) => [
      ...list,
      { id: ++counter, from: 'person', text: trimmed },
      {
        id: ++counter,
        from: 'assistant',
        text: ASSISTANT_UNAVAILABLE_REPLY,
      },
    ])
    setValue('')
  }

  return (
    <Sheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Помічник"
      description="Готує чернетки. Записує завжди людина."
      scrimStyle="companion"
      icon={<IconSparkles class="size-4" stroke-width={1.5} aria-hidden="true" />}
      bodyClass="flex flex-col gap-4 p-4"
      initialFocus="[data-assistant-autofocus]"
    >
      <div class="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto">
        <Show
          when={messages().length > 0}
          fallback={
            <div class="flex flex-wrap gap-2">
              <p class="w-full text-caption font-semibold text-text-subtle">Можна спитати</p>
              <For each={SUGGESTIONS()}>
                {(suggestion) => (
                  <button
                    type="button"
                    onClick={() =>
                      suggestion.mode === 'fill'
                        ? setValue(suggestion.prompt)
                        : submit(suggestion.prompt)
                    }
                    class="min-h-9 rounded-full border border-border px-3 py-2 text-left text-body text-text transition-[border-color,background-color] duration-(--duration-press) hover:border-border-strong hover:bg-surface-muted max-md:min-h-(--size-touch)"
                  >
                    {suggestion.title}
                  </button>
                )}
              </For>
            </div>
          }
        >
          <For each={messages()}>
            {(message) => (
              <div
                class={cn(
                  'max-w-[85%] rounded-panel px-3 py-2 text-body break-words whitespace-pre-wrap',
                  message.from === 'person'
                    ? 'ml-auto rounded-[0.875rem] bg-surface-sunken text-text'
                    : 'border border-draft-border bg-draft-soft text-text',
                )}
              >
                <Show when={message.from !== 'person'} fallback={message.text}>
                  <AiMarkdown text={message.text} class="whitespace-normal" />
                </Show>
              </div>
            )}
          </For>
        </Show>
      </div>

      <AiComposer
        value={value()}
        onValueChange={setValue}
        onSubmit={submit}
        minRows={3}
        footer={
          <>
            <AssistantTools onChoose={setValue} />
            <span class="inline-flex min-w-0 items-center gap-1 rounded-full bg-surface-sunken px-2 py-1 text-caption text-text-muted">
              <IconAt class="size-3.5 shrink-0" stroke-width={1.5} aria-hidden="true" />
              <span class="truncate">{contextLabel(location.pathname)}</span>
            </span>
          </>
        }
      />
    </Sheet>
  )
}
